import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiEdit2, FiTrash2, FiSearch, FiPlus, FiCalendar, FiImage, FiTag, FiFolder } from 'react-icons/fi';
import Card from '../../components/Card/Card';
import Badge from '../../components/Badge/Badge';
import Button from '../../components/Button/Button';
import DataTable from '../../components/DataTable/DataTable';
import Modal from '../../components/Modal/Modal';
import Input from '../../components/Input/Input';
import Select from '../../components/Select/Select';
import api from '../../api';
import './Notices.css';

const INITIAL_FORM = {
  category: '',
  categoryIcon: '',
  date: '',
  title: '',
  tag: '',
  tagIcon: '',
  image_url: '',
  isNew: '0'
};

const Notices = ({ showAlert, showConfirm }) => {
  const [notices, setNotices] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const fetchNotices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notices.php');
      setNotices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showAlert("Sync Error", err.response?.data?.error || "Failed to load notices list.", "confirm");
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.category || !form.date || !form.title) {
      showAlert("Required fields", "Please fill in Category, Date, and Title.", "confirm");
      return;
    }

    if (!imageFile && !form.image_url) {
      showAlert("Notice image required", "Please upload an image for this notice.", "confirm");
      return;
    }

    try {
      const formData = new FormData();
      formData.append('category', form.category);
      formData.append('categoryIcon', form.categoryIcon || '');
      formData.append('date', form.date);
      formData.append('title', form.title);
      formData.append('tag', form.tag || '');
      formData.append('tagIcon', form.tagIcon || '');
      formData.append('isNew', form.isNew);
      formData.append('image_url', form.image_url || '');

      if (imageFile) {
        formData.append('image', imageFile);
      }

      const headers = { 'Content-Type': 'multipart/form-data' };

      if (editingId) {
        formData.append('id', editingId);
        formData.append('_method', 'PUT');
        await api.post('/notices.php', formData, { headers });
        showAlert("Success", "Notice updated successfully!", "success");
      } else {
        await api.post('/notices.php', formData, { headers });
        showAlert("Success", "Notice added successfully!", "success");
      }

      setShowModal(false);
      setEditingId(null);
      setImageFile(null);
      setForm(INITIAL_FORM);
      fetchNotices();
    } catch (err) {
      showAlert("Process failed", err.response?.data?.error || "Action could not be completed.", "confirm");
    }
  };

  const handleDelete = async (id) => {
    showConfirm("Delete Notice", "Are you sure you want to delete this notice? This action is permanent.", async () => {
      try {
        await api.delete(`/notices.php?id=${id}`);
        fetchNotices();
        showAlert("Deleted", "Notice has been removed.", "success");
      } catch (err) {
        showAlert("Delete failed", err.response?.data?.error || "Could not delete notice.", "confirm");
      }
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filtered = useMemo(() => {
    let data = notices.filter(n =>
      n.title?.toLowerCase().includes(search.toLowerCase()) ||
      n.category?.toLowerCase().includes(search.toLowerCase()) ||
      n.tag?.toLowerCase().includes(search.toLowerCase())
    );

    if (sortConfig.key) {
      data.sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [notices, search, sortConfig]);

  const columns = [
    { key: 'title', label: 'Notice Details', sortable: true, render: (val, row) => {
      const imgUrl = row.image_url.startsWith('http') || row.image_url.startsWith('/') 
        ? row.image_url 
        : `${api.defaults.baseURL}/${row.image_url}`;
      return (
        <div className="notice-details-cell">
          <img 
            className="notice-list-thumb" 
            src={imgUrl} 
            alt="Notice Thumbnail" 
            onError={(e) => { e.target.style.display = 'none'; }} 
          />
          <div className="notice-icon-circle">
            <i className={row.categoryIcon || 'fa-solid fa-bullhorn'} />
          </div>
          <div className="notice-meta-stack">
            <span className="notice-title" title={val}>{val}</span>
            <span className="notice-category-badge">{row.category}</span>
          </div>
        </div>
      );
    }},
    { key: 'date', label: 'Notice Date', sortable: true, render: (val) => (
      <span className="notice-date-cell"><FiCalendar /> {new Date(val).toLocaleDateString()}</span>
    )},
    { key: 'tag', label: 'Tag Info', render: (val, row) => (
      val ? (
        <span className="notice-tag-badge">
          {row.tagIcon && <i className={`${row.tagIcon} notice-tag-icon`} />} {val}
        </span>
      ) : <span className="text-muted">—</span>
    )},
    { key: 'isNew', label: 'Status', sortable: true, render: (val) => (
      parseInt(val) === 1 ? (
        <Badge variant="danger" dot>New</Badge>
      ) : (
        <Badge variant="neutral">Archived</Badge>
      )
    )},
    { key: 'actions', label: 'Actions', render: (_, row) => (
      <div className="table-actions">
        <button className="table-action-btn table-action-btn--edit" onClick={() => { setEditingId(row.id); setForm({ ...row, isNew: String(row.isNew) }); setImageFile(null); setShowModal(true); }}><FiEdit2 /></button>
        <button className="table-action-btn table-action-btn--delete" onClick={() => handleDelete(row.id)}><FiTrash2 /></button>
      </div>
    )}
  ];

  return (
    <div className="notices-page">
      <div className="page-header">
        <div>
          <h2 className="page-header__title">Notice Board</h2>
          <p className="page-header__sub">Broadcast library alerts, news, events and warnings</p>
        </div>
        <Button variant="primary" icon={<FiPlus />} onClick={() => { setEditingId(null); setForm(INITIAL_FORM); setImageFile(null); setShowModal(true); }}>Add Notice</Button>
      </div>

      <Card padding="none">
        <div className="shared-toolbar">
          <div className="shared-search-box">
            <FiSearch className="shared-search-box__icon" />
            <input
              type="text"
              placeholder="Search by title, category, or tag..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="shared-search-box__input"
            />
          </div>
        </div>
        <DataTable columns={columns} data={filtered} loading={loading} onSort={handleSort} sortConfig={sortConfig} />
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingId ? 'Edit Notice' : 'Add New Notice'}>
        <form onSubmit={handleSubmit} className="professional-form">
          <div className="form-grid form-grid--2">
            <Input id="title" name="title" label="Notice Title" value={form.title} onChange={handleChange} required className="form-grid__span-2" />
            
            <Input id="category" name="category" label="Category" value={form.category} onChange={handleChange} placeholder="e.g. General, Exams, Closed" required prefix={<FiFolder />} />
            <Input id="categoryIcon" name="categoryIcon" label="Category Icon Class" value={form.categoryIcon} onChange={handleChange} placeholder="e.g. fa-solid fa-circle-info" />
            
            <Input id="date" name="date" label="Notice Date" type="date" value={form.date} onChange={handleChange} required prefix={<FiCalendar />} />
            <Select id="isNew" name="isNew" label="Mark as NEW?" options={[{ value: '0', label: 'No (Archived)' }, { value: '1', label: 'Yes (Active/New)' }]} value={form.isNew} onChange={handleChange} />
            
            <Input id="tag" name="tag" label="Tag Badge" value={form.tag} onChange={handleChange} placeholder="e.g. Urgent, Free" prefix={<FiTag />} />
            <Input id="tagIcon" name="tagIcon" label="Tag Icon Class" value={form.tagIcon} onChange={handleChange} placeholder="e.g. fa-solid fa-triangle-exclamation" />
            
            <div className="input-group form-grid__span-2">
              <label className="input-label">Notice Banner Image</label>
              <div className="file-uploader">
                <input
                  type="file"
                  id="notice-image-upload"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0] || null)}
                  style={{ display: 'none' }}
                />
                <label htmlFor="notice-image-upload" className="file-uploader__label">
                  <FiImage className="file-uploader__icon" />
                  <span>
                    {imageFile 
                      ? `Selected: ${imageFile.name}` 
                      : (form.image_url ? 'Change Notice Image' : 'Choose Notice Image File')}
                  </span>
                </label>
                {(imageFile || form.image_url) && (
                  <div className="file-uploader__preview">
                    <img
                      src={imageFile 
                        ? URL.createObjectURL(imageFile) 
                        : (form.image_url.startsWith('http') || form.image_url.startsWith('/') 
                          ? form.image_url 
                          : `${api.defaults.baseURL}/${form.image_url}`)}
                      alt="Notice Preview"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="form-actions-bar">
            <Button type="submit" variant="primary" icon={editingId ? <FiEdit2 /> : <FiPlus />}>{editingId ? 'Save Changes' : 'Add Notice'}</Button>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Notices;
